<script>
  import { onDestroy, onMount } from 'svelte';
  import { getApiBase } from './apiBase.js';
  import { enqueueCalendarRefresh, pollCalendarRefresh } from './calendarRefreshApi.js';

  let message = '';
  let loading = false;
  let errorMessage = '';
  let selectedTeacher = null; // Stores selected teacher object
  let teacherQuery = ''; // Stores search query
  let filteredTeachers = []; // Stores filtered teachers
  let showTeacherDropdown = false; // Controls dropdown visibility
  let onlyRefreshSelected = false; // Stores checkbox state
  let refreshType = 'current_month'; // New variable for refresh type
  let teachers = []; // Stores teachers with their names and Google Calendar IDs
  let warnings = null; // Stores skipped events warnings
  let classesUploaded = 0; // Count of classes uploaded
  let classesSkipped = 0; // Count of classes skipped
  let calendarFailures = [];
  let refreshProgress = null;
  let pollingController = null;
  const activeRefreshKey = 'activeCalendarRefreshStatusUrl';
  
  // Month selection variables - default to current month
  const currentDate = new Date();
  let selectedMonth = currentDate.getMonth() + 1; // Current month (1-12)
  let selectedYear = currentDate.getFullYear(); // Current year
  
  // Day selection variable
  let selectedDate = ''; // Format: YYYY-MM-DD
  
  // Week selection variables
  let selectedWeekMonth = currentDate.getMonth() + 1; // Current month for week selection
  let selectedWeekYear = currentDate.getFullYear(); // Current year for week selection
  let selectedWeekOption = ''; // Selected week option (week1, week2, etc.)
  let weekOptions = []; // Stores week options for the selected month
  
  // Generate month options starting from September 2025
  function generateMonthOptions() {
    const months = [
      { value: 1, name: 'January' },
      { value: 2, name: 'February' },
      { value: 3, name: 'March' },
      { value: 4, name: 'April' },
      { value: 5, name: 'May' },
      { value: 6, name: 'June' },
      { value: 7, name: 'July' },
      { value: 8, name: 'August' },
      { value: 9, name: 'September' },
      { value: 10, name: 'October' },
      { value: 11, name: 'November' },
      { value: 12, name: 'December' }
    ];
    
    const options = [];
    const startYear = 2025;
    const startMonth = 9; // September
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    // Add months from September 2025 onwards
    for (let year = startYear; year <= currentYear + 2; year++) {
      for (let month = 1; month <= 12; month++) {
        // Skip months before September 2025
        if (year === startYear && month < startMonth) continue;
        // Don't go too far into the future
        if (year > currentYear + 2) break;
        
        const monthName = months.find(m => m.value === month).name;
        options.push({
          value: `${year}-${month}`,
          label: `${monthName} ${year}`,
          year: year,
          month: month
        });
      }
    }
    
    return options;
  }
  
  let monthOptions = generateMonthOptions();
  let selectedMonthYear = `${selectedYear}-${selectedMonth}`;

  const apiBase = getApiBase();

  // Helper function to get today's date in YYYY-MM-DD format
  function getTodayDate() {
    const today = new Date();
    return today.getFullYear() + '-' + 
           (today.getMonth() + 1).toString().padStart(2, '0') + '-' + 
           today.getDate().toString().padStart(2, '0');
  }

  // Generate week options for a specific month
  function generateWeekOptions(year, month) {
    const weeks = [];
    const firstDayOfMonth = new Date(year, month - 1, 1); // month is 0-indexed in JS Date
    const lastDayOfMonth = new Date(year, month, 0); // Day 0 of next month = last day of current month
    
    // Start from the first Monday within or overlapping the month
    let currentMonday = new Date(firstDayOfMonth);
    const firstDayWeekday = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Adjust to find the first Monday that creates a week overlapping this month
    if (firstDayWeekday === 0) { // If month starts on Sunday
      currentMonday.setDate(firstDayOfMonth.getDate() + 1); // Monday is the next day
    } else if (firstDayWeekday === 1) { // If month starts on Monday
      // currentMonday is already correct (first day of month)
    } else { // Month starts Tue-Sat, find the previous Monday
      const daysBack = firstDayWeekday - 1;
      currentMonday.setDate(firstDayOfMonth.getDate() - daysBack);
    }
    
    let weekNumber = 1;
    
    // Generate weeks while the Monday is within reasonable range
    while (currentMonday <= lastDayOfMonth) {
      const sunday = new Date(currentMonday);
      sunday.setDate(currentMonday.getDate() + 6);
      
      // Only include weeks that have days within the target month
      if (sunday >= firstDayOfMonth) {
        // Calculate the portion of the week that falls within the month
        const weekStart = currentMonday < firstDayOfMonth ? firstDayOfMonth : currentMonday;
        const weekEnd = sunday > lastDayOfMonth ? lastDayOfMonth : sunday;
        
        // Convert to YYYY-MM-DD format without timezone issues
        const startStr = weekStart.getFullYear() + '-' + 
                         (weekStart.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                         weekStart.getDate().toString().padStart(2, '0');
        const endStr = weekEnd.getFullYear() + '-' + 
                      (weekEnd.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                      weekEnd.getDate().toString().padStart(2, '0');
        
        weeks.push({
          value: `week${weekNumber}`,
          label: `Week ${weekNumber}: ${startStr} to ${endStr}`,
          startDate: startStr,
          endDate: endStr,
          mondayDate: currentMonday.getFullYear() + '-' + 
                     (currentMonday.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                     currentMonday.getDate().toString().padStart(2, '0')
        });
        
        weekNumber++;
      }
      
      // Move to next Monday
      currentMonday.setDate(currentMonday.getDate() + 7);
    }
    
    return weeks;
  }

  // Update week options when month/year changes
  function updateWeekOptions() {
    weekOptions = generateWeekOptions(selectedWeekYear, selectedWeekMonth);
    if (weekOptions.length > 0) {
      selectedWeekOption = weekOptions[0].value; // Default to first week
    }
  }

  // Initialize default dates
  selectedDate = getTodayDate();

  async function fetchTeachers() {
      const url = `${apiBase}/studentplayground/api/get_google_calendar_ids/`;
      const token = localStorage.getItem('jwtToken');
      if (!token) {
          errorMessage = 'JWT token not found. Please log in.';
          return;
      }

      try {
          const response = await fetch(url, {
              method: 'GET',
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
              },
          });

          if (!response.ok) {
              throw new Error(`Error: ${response.status}`);
          }

          const data = await response.json();
          teachers = data.teachers; // Assuming backend returns { "teachers": [...] }
          filteredTeachers = teachers;
      } catch (error) {
          errorMessage = `Failed to fetch teachers: ${error.message}`;
      }
  }

  // Filter teachers based on search query
  function filterTeachers() {
    if (!teacherQuery.trim()) {
      filteredTeachers = teachers;
      showTeacherDropdown = false;
    } else {
      const query = teacherQuery.toLowerCase();
      filteredTeachers = teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(query) ||
        (teacher.email && teacher.email.toLowerCase().includes(query))
      );
      // Only show dropdown if no teacher is selected and there are filtered results
      showTeacherDropdown = !selectedTeacher && filteredTeachers.length > 0;
    }
  }

  // Handle teacher selection
  function selectTeacher(teacher) {
    selectedTeacher = teacher;
    teacherQuery = teacher.name;
    showTeacherDropdown = false;
  }

  // Clear teacher selection
  function clearTeacherSelection() {
    selectedTeacher = null;
    teacherQuery = '';
    showTeacherDropdown = false;
  }

  function applyRefreshJob(job) {
    message = job.message || 'Calendar refresh in progress';
    refreshProgress = job.progress || null;
    classesUploaded = job.classes_uploaded || 0;
    classesSkipped = job.classes_skipped || 0;
    warnings = job.warnings || null;
    calendarFailures = job.failures || [];
    if (job.status === 'failed') {
      errorMessage = job.message || 'Database refresh failed.';
    }
  }

  async function followRefresh(statusUrl, token) {
    pollingController?.abort();
    pollingController = new AbortController();
    sessionStorage.setItem(activeRefreshKey, statusUrl);
    loading = true;
    try {
      const finalJob = await pollCalendarRefresh({
        statusUrl,
        token,
        signal: pollingController.signal,
        onUpdate: applyRefreshJob,
      });
      applyRefreshJob(finalJob);
      sessionStorage.removeItem(activeRefreshKey);
    } catch (error) {
      if (error.name !== 'AbortError') {
        errorMessage = `Failed to check refresh progress: ${error.message}`;
      }
    } finally {
      loading = false;
    }
  }

  async function refreshDatabase() {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
          errorMessage = 'JWT token not found. Please log in.';
          return;
      }

      loading = true;
      message = '';
      errorMessage = '';
      warnings = null; // Clear previous warnings
      classesUploaded = 0;
      classesSkipped = 0;
      calendarFailures = [];
      refreshProgress = null;

      // Parse selected month and year from the dropdown
      const selectedOption = monthOptions.find(option => option.value === selectedMonthYear);
      const monthToRefresh = selectedOption ? selectedOption.month : selectedMonth;
      const yearToRefresh = selectedOption ? selectedOption.year : selectedYear;
      
      // Get selected week data for week refresh
      const selectedWeek = weekOptions.find(option => option.value === selectedWeekOption);
      const weekStartDate = selectedWeek ? selectedWeek.startDate : null;
      const weekEndDate = selectedWeek ? selectedWeek.endDate : null;

      try {
          const job = await enqueueCalendarRefresh({
              apiBase,
              token,
              payload: {
                  only_refresh_selected: onlyRefreshSelected,
                  google_calendar_id: onlyRefreshSelected && selectedTeacher ? selectedTeacher.googleCalendarId : null,
                  refresh_type: refreshType,
                  selected_month: monthToRefresh,
                  selected_year: yearToRefresh,
                  selected_date: refreshType === 'day' ? selectedDate : null,
                  selected_week_start: refreshType === 'week' ? weekStartDate : null,
                  selected_week_end: refreshType === 'week' ? weekEndDate : null
              },
          });
          applyRefreshJob(job);
          await followRefresh(job.status_url, token);
      } catch (error) {
          errorMessage = `Failed to update: ${error.message}`;
          loading = false;
      }
  }

  // Reactive statement to filter teachers when search query changes
  $: if (teacherQuery !== undefined) {
    filterTeachers();
  }

  // Reactive statement to update week options when month/year changes
  $: if (selectedWeekMonth && selectedWeekYear) {
    updateWeekOptions();
  }

  onMount(() => {
    updateWeekOptions();
    fetchTeachers();
    const token = localStorage.getItem('jwtToken');
    const statusUrl = sessionStorage.getItem(activeRefreshKey);
    if (token && statusUrl) followRefresh(statusUrl, token);
  });

  onDestroy(() => pollingController?.abort());
</script>

<!-- HTML/Markup -->
<div class="refresh-database-container">
  <h3>Refresh Database</h3>
  
  <!-- Add radio buttons for refresh type -->
  <div class="refresh-options">
    <label>
      <input type="radio" bind:group={refreshType} value="current_month" />
      Refresh Month
    </label>
    <label>
      <input type="radio" bind:group={refreshType} value="week" />
      Refresh Week
    </label>
    <label>
      <input type="radio" bind:group={refreshType} value="day" />
      Refresh Day
    </label>
    <!-- Temporarily commented out academic year option
    <label>
      <input type="radio" bind:group={refreshType} value="academic_year" />
      Refresh Academic Year
    </label>
    -->
  </div>

  <!-- Month selection for current month refresh -->
  {#if refreshType === 'current_month'}
    <div class="month-selection-section">
      <label for="month-select">
        <strong>Select Month to Refresh:</strong>
      </label>
      <select 
        id="month-select" 
        bind:value={selectedMonthYear}
        class="month-select"
      >
        {#each monthOptions as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </div>
  {/if}

  <!-- Day selection for day refresh -->
  {#if refreshType === 'day'}
    <div class="date-selection-section">
      <label for="day-select">
        <strong>Select Day to Refresh:</strong>
      </label>
      <input 
        id="day-select" 
        type="date" 
        bind:value={selectedDate}
        class="date-input"
      />
    </div>
  {/if}

  <!-- Week selection for week refresh -->
  {#if refreshType === 'week'}
    <div class="week-selection-section">
      <label for="week-month-select">
        <strong>Select Month:</strong>
      </label>
      <div class="month-year-selectors">
        <select 
          id="week-month-select" 
          bind:value={selectedWeekMonth}
          class="month-year-select"
        >
          <option value={1}>January</option>
          <option value={2}>February</option>
          <option value={3}>March</option>
          <option value={4}>April</option>
          <option value={5}>May</option>
          <option value={6}>June</option>
          <option value={7}>July</option>
          <option value={8}>August</option>
          <option value={9}>September</option>
          <option value={10}>October</option>
          <option value={11}>November</option>
          <option value={12}>December</option>
        </select>
        
        <select 
          bind:value={selectedWeekYear}
          class="month-year-select"
        >
          <option value={2024}>2024</option>
          <option value={2025}>2025</option>
          <option value={2026}>2026</option>
          <option value={2027}>2027</option>
        </select>
      </div>
      
      <label for="week-option-select">
        <strong>Select Week:</strong>
      </label>
      <select 
        id="week-option-select" 
        bind:value={selectedWeekOption}
        class="week-select"
      >
        {#each weekOptions as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </div>
  {/if}

  <!-- Show teacher selection for all refresh types -->
  {#if refreshType === 'current_month' || refreshType === 'day' || refreshType === 'week'}
    <div class="teacher-selection-section">
      <label>
        <input type="checkbox" bind:checked={onlyRefreshSelected} />
        Refresh only for a specific teacher
      </label>

      {#if onlyRefreshSelected}
        <div class="input-container">
          <input 
            type="text" 
            placeholder="Search teacher by name..."
            bind:value={teacherQuery}
            on:input={() => {
              if (selectedTeacher && teacherQuery !== selectedTeacher.name) {
                selectedTeacher = null;
                showTeacherDropdown = true;
              } else if (!selectedTeacher) {
                showTeacherDropdown = teacherQuery.trim().length > 0;
              }
            }}
            autocomplete="off"
            class="search-input"
          />
          
          {#if showTeacherDropdown}
            <ul class="autocomplete-list">
              {#each filteredTeachers.slice(0, 8) as teacher}
                <li>
                  <button
                    type="button"
                    on:click={() => selectTeacher(teacher)}
                    on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
                  >
                    <div class="teacher-option">
                      <span class="teacher-name">{teacher.name}</span>
                      {#if teacher.email}
                        <span class="teacher-email">{teacher.email}</span>
                      {/if}
                    </div>
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
        
        {#if selectedTeacher}
          <div class="selected-teacher">
            <div class="selected-teacher-info">
              <strong>Selected Teacher:</strong> {selectedTeacher.name}
              {#if selectedTeacher.email}
                <br><small>Email: {selectedTeacher.email}</small>
              {/if}
            </div>
            
            <button 
              type="button" 
              class="btn-clear" 
              on:click={clearTeacherSelection}
            >
              Clear Selection
            </button>
          </div>
        {/if}
      {/if}
    </div>
  {/if}

  <button 
    class="refresh-button" 
    on:click={refreshDatabase} 
    disabled={loading || 
              (onlyRefreshSelected && !selectedTeacher) ||
              (refreshType === 'day' && !selectedDate) ||
              (refreshType === 'week' && !selectedWeekOption)}
  >
    {loading ? 'Updating...' : 'Refresh Database'}
  </button>

  {#if loading && refreshProgress}
    <div class="progress-message" role="status">
      Refreshed {refreshProgress.processed} of {refreshProgress.total} calendars
      {#if refreshProgress.failed > 0}
        ({refreshProgress.failed} failed)
      {/if}
    </div>
  {/if}

  <!-- Display success message -->
  {#if message}
    <div class="success-message" class:has-warnings={(warnings && classesSkipped > 0) || calendarFailures.length > 0}>
      {message}
      {#if classesUploaded > 0}
        <div class="stats-info">
          <span>✓ {classesUploaded} classes uploaded successfully</span>
        </div>
      {/if}
      {#if calendarFailures.length > 0}
        <div class="stats-info">
          {calendarFailures.length} calendar(s) could not be refreshed. You can retry the same refresh safely.
        </div>
      {/if}
    </div>
  {/if}

  <!-- Display warning message for skipped events -->
  {#if warnings}
    <div class="warning-message">
      <div class="warning-header">
        <span class="warning-icon">⚠</span>
        <strong>WARNING: {warnings.total_skipped} events were skipped during refresh</strong>
      </div>
      
      <div class="warning-stats">
        {#if warnings.missing_location > 0}
          <div class="stat-item">📍 Missing location: {warnings.missing_location}</div>
        {/if}
        {#if warnings.missing_description > 0}
          <div class="stat-item">📝 Missing description: {warnings.missing_description}</div>
        {/if}
        {#if warnings.missing_summary > 0}
          <div class="stat-item">📋 Missing summary: {warnings.missing_summary}</div>
        {/if}
        {#if warnings.missing_organizer > 0}
          <div class="stat-item">👤 Missing organizer: {warnings.missing_organizer}</div>
        {/if}
      </div>

      <div class="skipped-events-list">
        <div class="skipped-header">Skipped Classes:</div>
        {#each warnings.skipped_events as event}
          <div class="skipped-event-item">
            <div class="event-summary">{event.summary}</div>
            <div class="event-details">
              <span class="event-date">📅 {event.date}</span>
              <span class="event-time">🕒 {event.time}</span>
              <span class="event-missing">Missing: {event.missing_fields.join(', ')}</span>
            </div>
          </div>
        {/each}
        {#if warnings.total_skipped > warnings.skipped_events.length}
          <div class="more-events-note">
            ... and {warnings.total_skipped - warnings.skipped_events.length} more events
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Display error message -->
  {#if errorMessage}
    <div class="error-message">{errorMessage}</div>
  {/if}
</div>

<style>
  .refresh-database-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
  }

  .refresh-database-container h3 {
    margin: 0 0 20px 0;
    color: #333;
    text-align: center;
  }

  .refresh-options {
    margin-bottom: 20px;
    text-align: center;
  }

  .refresh-options label {
    margin-right: 20px;
    cursor: pointer;
  }

  .month-selection-section {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .month-selection-section label {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
    color: #333;
  }

  .month-select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
  }

  .month-select:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  .date-selection-section {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .date-selection-section label {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
    color: #333;
  }

  .date-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
  }

  .date-input:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  .week-selection-section {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .week-selection-section label {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
    color: #333;
  }

  .month-year-selectors {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
  }

  .month-year-select {
    flex: 1;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
  }

  .month-year-select:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  .week-select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
  }

  .week-select:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  .teacher-selection-section {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .teacher-selection-section label {
    display: block;
    margin-bottom: 15px;
    font-weight: 500;
  }

  .input-container {
    position: relative;
    margin-top: 15px;
  }

  .search-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
  }

  .search-input:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
    padding: 0;
    margin: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .autocomplete-list li {
    border-bottom: 1px solid #f0f0f0;
  }

  .autocomplete-list li:last-child {
    border-bottom: none;
  }

  .autocomplete-list button {
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .autocomplete-list button:hover {
    background-color: #f5f5f5;
  }

  .teacher-option {
    display: flex;
    flex-direction: column;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
  }

  .teacher-email {
    font-size: 0.8em;
    color: #666;
    margin-top: 2px;
  }

  .selected-teacher {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #e8f5e8;
    padding: 15px;
    border-radius: 6px;
    margin-top: 15px;
    border: 1px solid #c8e6c9;
  }

  .selected-teacher-info {
    color: #2e7d32;
  }

  .btn-clear {
    background-color: #f44336;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
  }

  .btn-clear:hover {
    background-color: #d32f2f;
  }

  .refresh-button {
    width: 100%;
    padding: 12px 20px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    transition: background-color 0.2s ease;
  }

  .refresh-button:hover:not(:disabled) {
    background-color: #45a049;
  }

  .refresh-button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }

  .progress-message {
    margin-top: 12px;
    color: #1f4b75;
    text-align: center;
    font-size: 0.9em;
  }

  .success-message {
    color: #2e7d32;
    background: #e8f5e8;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #2e7d32;
    margin-top: 20px;
    font-size: 0.9em;
  }

  .success-message.has-warnings {
    color: #ed6c02;
    background: #fff4e5;
    border-left-color: #ed6c02;
  }

  .stats-info {
    margin-top: 8px;
    font-size: 0.85em;
    opacity: 0.9;
  }

  .warning-message {
    color: #ed6c02;
    background: #fff4e5;
    padding: 15px;
    border-radius: 6px;
    border-left: 4px solid #ed6c02;
    margin-top: 15px;
    font-size: 0.9em;
  }

  .warning-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .warning-icon {
    font-size: 1.2em;
  }

  .warning-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 8px;
    margin-bottom: 15px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 4px;
  }

  .stat-item {
    font-size: 0.85em;
    padding: 5px;
  }

  .skipped-events-list {
    margin-top: 12px;
    max-height: 400px;
    overflow-y: auto;
    border-top: 1px solid rgba(237, 108, 2, 0.3);
    padding-top: 12px;
  }

  .skipped-header {
    font-weight: 600;
    margin-bottom: 10px;
    font-size: 0.95em;
  }

  .skipped-event-item {
    background: white;
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 8px;
    border: 1px solid rgba(237, 108, 2, 0.2);
  }

  .event-summary {
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    font-size: 0.95em;
  }

  .event-details {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 0.8em;
    color: #666;
  }

  .event-date,
  .event-time {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .event-missing {
    color: #d32f2f;
    font-weight: 500;
  }

  .more-events-note {
    margin-top: 10px;
    font-style: italic;
    text-align: center;
    color: #666;
    font-size: 0.85em;
  }

  .error-message {
    color: #d32f2f;
    background: #ffebee;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #d32f2f;
    margin-top: 20px;
    font-size: 0.9em;
  }

  /* Mobile responsiveness */
  @media (max-width: 600px) {
    .refresh-database-container {
      padding: 15px;
    }

    .refresh-options {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .refresh-options label {
      margin-bottom: 0;
      margin-right: 0;
    }

    .selected-teacher {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .warning-stats {
      grid-template-columns: 1fr;
    }

    .event-details {
      flex-direction: column;
      gap: 6px;
    }

    .skipped-events-list {
      max-height: 300px;
    }
  }
</style>
